import React from "react";
import { ReactComponent as WallSvg } from "../../Assets/wall.svg";
import DoorRenderer from "./DoorRenderer";
import WindowRenderer from "./WindowRenderer";
import PanoramicWindowRenderer from "./PanoramicWindowRenderer";
import OutletRenderer from "./OutletRenderer";
import Outlet2Renderer from "./Outlet2Renderer";
import SwitchRenderer from "./SwitchRenderer";
import SwitchDoubleRenderer from "./SwitchDoubleRenderer";
import RadiatorRenderer from "./RadiatorRenderer";
import KitchenElementRenderer from "./KitchenElementRenderer";
import HeaterRenderer from "./HeaterRenderer";
import ToiletRenderer from "./ToiletRenderer";
import WaterHeaterRenderer from "./WaterHeaterRenderer";
import HandBasinRenderer from "./HandBasinRenderer";
import AirConditionerInternalRenderer from "./AirConditionerInternalRenderer";
import AirConditionerExternalRenderer from "./AirConditionerExternalRenderer";
import ShowerRenderer from "./ShowerRenderer";
import GateRenderer from "./GateRenderer";
import WindowBlindsRenderer from "./WindowBlindsRenderer";
import CustomElementRenderer from "./CustomElementRenderer";
import {
    OBJECT_TYPES,
    WALL_THICKNESS_M_RENDER,
    CORRIDOR_WALL_THICKNESS_M,
    SELECTED_ELEMENT_COLOR,
    PLACEMENT_HIGHLIGHT_FILL_COLOR,
    defaultObjectSizes,
    GRID_CELL_SIZE_M,
} from "../configuratorConstants";
import { subtractVectors, distance } from "../configuratorUtils";

const FramelessWallRenderer = ({
    wall,
    scale,
    isSelected,
    selectedElementId,
    onSelectWall,
    onSelectElement,
    onContextMenu,
    isInPlacementMode,
    highlightForPlacement,
    onWallSegmentClick,
    objectType = OBJECT_TYPES.FRAMELESS_WALL,
    elementTypeToPlace,
}) => {
    const { startPoint, endPoint, segments = [], id } = wall;

    const wallVector = subtractVectors(endPoint, startPoint);
    const wallAngle = Math.atan2(wallVector.y, wallVector.x) * (180 / Math.PI);
    const wallLengthPx = distance(startPoint, endPoint) * scale;
    // Используем CORRIDOR_WALL_THICKNESS_M для внутренних стен, WALL_THICKNESS_M_RENDER для обычных
    const isInternalWall = wall.isInternal === true;
    const wallThicknessPx = (isInternalWall ? CORRIDOR_WALL_THICKNESS_M : WALL_THICKNESS_M_RENDER) * scale;
    const orientation = (Math.abs(wallVector.x) > Math.abs(wallVector.y)) ? 'h' : 'v';

    const renderers = {
        [OBJECT_TYPES.DOOR]: DoorRenderer,
        [OBJECT_TYPES.WINDOW]: WindowRenderer,
        [OBJECT_TYPES.PANORAMIC_WINDOW]: PanoramicWindowRenderer,
        [OBJECT_TYPES.OUTLET]: OutletRenderer,
        [OBJECT_TYPES.OUTLET_DOUBLE]: Outlet2Renderer,
        [OBJECT_TYPES.SWITCH]: SwitchRenderer,
        [OBJECT_TYPES.SWITCH_DOUBLE]: SwitchDoubleRenderer,
        [OBJECT_TYPES.RADIATOR]: RadiatorRenderer,
        [OBJECT_TYPES.KITCHEN_ELEMENT]: KitchenElementRenderer,
        [OBJECT_TYPES.HEATER]: HeaterRenderer,
        [OBJECT_TYPES.TOILET]: ToiletRenderer,
        [OBJECT_TYPES.WATER_HEATER]: WaterHeaterRenderer,
        [OBJECT_TYPES.HAND_BASIN]: HandBasinRenderer,
        [OBJECT_TYPES.INTERNAL_AIR_CONDITIONER]: AirConditionerInternalRenderer,
        [OBJECT_TYPES.EXTERNAL_AIR_CONDITIONER]: AirConditionerExternalRenderer,
        [OBJECT_TYPES.SHOWER]: ShowerRenderer,
        [OBJECT_TYPES.GATE]: GateRenderer,
        [OBJECT_TYPES.WINDOW_BLINDS]: WindowBlindsRenderer,
    };

    const isWallSelected = isSelected && !selectedElementId;

    let overlayFill = "none";
    if (isWallSelected) {
        overlayFill = "rgba(0, 123, 255, 0.3)";
    }

    return (
        <g
            transform={`translate(${startPoint.x * scale}, ${startPoint.y * scale}) rotate(${wallAngle})`}
            data-object-id={id}
            data-object-type={objectType}
            onContextMenu={(e) => onContextMenu(e, id, objectType)}
        >
            {segments.map((segment) => {
                const segmentStartPx = segment.start * scale;
                const segmentLengthPx = segment.length * scale;
                const isSegmentSelected = selectedElementId === segment.id;
                const currentElements = Array.isArray(segment.elements)
                    ? segment.elements
                    : [];

                // Сегменты скрываются под дверьми, воротами и панорамными окнами
                const isLargeOpeningElement = (type) =>
                    type === OBJECT_TYPES.DOOR ||
                    type === OBJECT_TYPES.GATE ||
                    type === OBJECT_TYPES.WINDOW ||
                    type === OBJECT_TYPES.PANORAMIC_WINDOW;

                // Фильтруем двери, ворота и панорамные окна (не обычные окна)
                const openingElements = currentElements
                    .filter((el) =>
                        el.type === OBJECT_TYPES.DOOR ||
                        el.type === OBJECT_TYPES.GATE ||
                        (el.type === OBJECT_TYPES.PANORAMIC_WINDOW && !el.isAdjacentCopy)
                    )
                    .sort(
                        (a, b) =>
                            (a.positionOnSegment || 0.5) - (b.positionOnSegment || 0.5),
                    );

                // Проверяем, есть ли панорамное окно на этом сегменте (оригинал или копия)
                const hasPanoramicWindow = currentElements.some(
                    el => el.type === OBJECT_TYPES.PANORAMIC_WINDOW
                );

                // Проверяем, есть ли ворота на этом сегменте (оригинал или копия)
                const hasGate = currentElements.some(
                    el => el.type === OBJECT_TYPES.GATE
                );

                if (process.env.NODE_ENV !== "production" && hasPanoramicWindow) {
                    console.log('🪟 [FramelessWallRenderer] Hiding segment due to panoramic window:', {
                        segmentId: segment.id,
                        wallId: id,
                        objectType: objectType,
                        elementsCount: currentElements.length,
                        panoramicWindows: currentElements.filter(el => el.type === OBJECT_TYPES.PANORAMIC_WINDOW).map(el => ({
                            id: el.id,
                            isAdjacentCopy: el.isAdjacentCopy
                        }))
                    });
                }

                if (process.env.NODE_ENV !== "production" && hasGate) {
                    console.log('🚪 [FramelessWallRenderer] Hiding segment due to gate:', {
                        segmentId: segment.id,
                        wallId: id,
                        objectType: objectType,
                        elementsCount: currentElements.length,
                        gates: currentElements.filter(el => el.type === OBJECT_TYPES.GATE).map(el => ({
                            id: el.id,
                            isAdjacentCopy: el.isAdjacentCopy
                        }))
                    });
                }

                // Если есть панорамное окно или ворота (оригинал или копия), полностью скрываем сегмент
                if (hasPanoramicWindow || hasGate) {
                    // Не рендерим сегмент стены, но рендерим элементы
                    return (
                        <g key={segment.id} data-segment-id={segment.id}>
                            <g
                                data-object-id={segment.id}
                                data-parent-wall-id={id}
                                data-object-type="frameless_wall_segment"
                                className="cursor-pointer"
                                style={{ cursor: "pointer" }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    // Если размещается ролета, не обрабатываем клик на сегменте стены
                                    // Ролета должна размещаться только при клике на окно
                                    if (elementTypeToPlace === OBJECT_TYPES.WINDOW_BLINDS) {
                                        return;
                                    }
                                    if (isInPlacementMode) {
                                        if (onWallSegmentClick) {
                                            onWallSegmentClick(segment.id);
                                        }
                                    } else {
                                        onSelectWall(segment.id);
                                    }
                                }}
                            >
                                {/* Сегмент стены не рендерится при панорамном окне или воротах */}
                            </g>
                            {/* Рендерим элементы */}
                            {currentElements
                                .filter((element) => {
                                    if (!element || !element.type) return false;
                                    // Don't render adjacent copies of panoramic windows
                                    if (element.isAdjacentCopy && element.type === OBJECT_TYPES.PANORAMIC_WINDOW) return false;
                                    // Don't render adjacent copies of gates
                                    if (element.isAdjacentCopy && element.type === OBJECT_TYPES.GATE) return false;
                                    return true;
                                })
                                .map((element) => {
                                    const positionOnSegment = element.positionOnSegment || 0.5;
                                    const elementCenterPx = segmentStartPx + positionOnSegment * segmentLengthPx;
                                    // Используем CustomElementRenderer для неизвестных типов
                                    const ElementComponent = renderers[element.type] || CustomElementRenderer;

                                    const basePlacementSide = element.placementSide === 0
                                        ? 1
                                        : element.placementSide ?? 1;
                                    const finalPlacementSideFactor = basePlacementSide;
                                    const windowShouldFlip = finalPlacementSideFactor === 1;

                                    const shouldHighlightWindowForBlinds = highlightForPlacement &&
                                        (element.type === OBJECT_TYPES.WINDOW || element.type === OBJECT_TYPES.PANORAMIC_WINDOW) &&
                                        !currentElements.some(el =>
                                            el.type === OBJECT_TYPES.WINDOW_BLINDS &&
                                            Math.abs((el.positionOnSegment || 0.5) - positionOnSegment) < 0.1
                                        );

                                    const associatedWindow = element.type === OBJECT_TYPES.WINDOW_BLINDS
                                        ? currentElements.find(
                                            w => (w.type === OBJECT_TYPES.WINDOW || w.type === OBJECT_TYPES.PANORAMIC_WINDOW) &&
                                                Math.abs((w.positionOnSegment || 0.5) - positionOnSegment) < 0.01
                                        )
                                        : null;

                                    return (
                                        <g
                                            key={element.id}
                                            transform={`translate(${elementCenterPx}, 0)`}
                                            onClick={(e) => {
                                                // Если размещается ролета, не блокируем событие - оно обработается в useMouseInteractions
                                                if (elementTypeToPlace === OBJECT_TYPES.WINDOW_BLINDS &&
                                                    (element.type === OBJECT_TYPES.WINDOW || element.type === OBJECT_TYPES.PANORAMIC_WINDOW)) {
                                                    // Не вызываем stopPropagation, чтобы событие дошло до useMouseInteractions
                                                    if (process.env.NODE_ENV !== "production") {
                                                        console.log('[FramelessWallRenderer] Window clicked for blinds placement, allowing event to bubble', {
                                                            windowId: element.id,
                                                            windowType: element.type,
                                                            segmentId: segment.id,
                                                            target: e.target,
                                                            currentTarget: e.currentTarget
                                                        });
                                                    }
                                                    // Не блокируем событие - оно должно всплыть до useMouseInteractions
                                                    // Но предотвращаем выбор элемента
                                                    // Не вызываем e.preventDefault() и e.stopPropagation()
                                                    return;
                                                }
                                                // Для остальных случаев блокируем событие
                                                e.stopPropagation();
                                                onSelectElement(element.id);
                                            }}
                                            onContextMenu={(e) =>
                                                onContextMenu(e, element.id, element.type)
                                            }
                                            style={{ cursor: "pointer", pointerEvents: "all" }}
                                            data-object-id={element.id}
                                            data-object-type={element.type}
                                            data-segment-id={segment.id}
                                            data-parent-wall-id={id}
                                        >
                                            {(() => {
                                                // Для панорамных окон и ролеты передаем isFrameless=true (безкаркасный режим)
                                                const isPanoramicWindow = element.type === OBJECT_TYPES.PANORAMIC_WINDOW;
                                                const isWindowBlinds = element.type === OBJECT_TYPES.WINDOW_BLINDS;
                                                const additionalProps = (isPanoramicWindow || isWindowBlinds) ? { isFrameless: true } : {};

                                                // Передаем segmentId и parentWallId для окон, чтобы их можно было найти при клике
                                                const windowProps = (element.type === OBJECT_TYPES.WINDOW || element.type === OBJECT_TYPES.PANORAMIC_WINDOW)
                                                    ? { segmentId: segment.id, parentWallId: id }
                                                    : {};

                                                return (
                                                    <ElementComponent
                                                        element={element}
                                                        scale={scale}
                                                        isSelected={selectedElementId === element.id}
                                                        wallThickness={isInternalWall ? CORRIDOR_WALL_THICKNESS_M : WALL_THICKNESS_M_RENDER}
                                                        placementSideFactor={finalPlacementSideFactor}
                                                        orientation={orientation}
                                                        windowVisualNeedsFlip={windowShouldFlip}
                                                        highlightForBlindsPlacement={shouldHighlightWindowForBlinds}
                                                        associatedWindow={associatedWindow}
                                                        {...additionalProps}
                                                        {...windowProps}
                                                    />
                                                );
                                            })()}
                                        </g>
                                    );
                                })}
                        </g>
                    );
                }

                let segmentsToRender = [
                    {
                        start: segmentStartPx,
                        end: segmentStartPx + segmentLengthPx,
                    },
                ];

                // Удаляем сегменты под дверьми, воротами и панорамными окнами
                openingElements.forEach((opening) => {
                    let openingWidthM;
                    if (opening.type === OBJECT_TYPES.PANORAMIC_WINDOW) {
                        // Панорамное окно занимает 2 сегмента (длина сегмента * 2)
                        // В безкаркасном режиме сегменты могут быть разной длины, используем длину текущего сегмента * 2
                        openingWidthM = segment.length * 2;
                    } else {
                        openingWidthM =
                            opening.width ||
                            defaultObjectSizes[opening.type]?.width ||
                            GRID_CELL_SIZE_M;
                    }

                    const openingWidthPx = openingWidthM * scale;
                    const centerPx =
                        segmentStartPx +
                        (opening.positionOnSegment || 0.5) * segmentLengthPx;
                    const openingStart = Math.max(
                        segmentStartPx,
                        centerPx - openingWidthPx / 2,
                    );
                    const openingEnd = Math.min(
                        segmentStartPx + segmentLengthPx,
                        centerPx + openingWidthPx / 2,
                    );

                    if (openingEnd <= openingStart) {
                        return;
                    }

                    // Для панорамных окон: если открытие перекрывается с сегментом, полностью удаляем сегмент
                    if (opening.type === OBJECT_TYPES.PANORAMIC_WINDOW) {
                        const hasOverlap = openingStart < segmentsToRender[0]?.end && openingEnd > segmentsToRender[0]?.start;
                        if (hasOverlap) {
                            // Полностью удаляем сегмент
                            segmentsToRender = [];
                            return;
                        }
                    }

                    const updated = [];
                    segmentsToRender.forEach((seg) => {
                        if (openingStart >= seg.end || openingEnd <= seg.start) {
                            updated.push(seg);
                            return;
                        }
                        if (openingStart > seg.start) {
                            updated.push({ start: seg.start, end: openingStart });
                        }
                        if (openingEnd < seg.end) {
                            updated.push({ start: openingEnd, end: seg.end });
                        }
                    });
                    segmentsToRender = updated;
                });

                return (
                    <g key={segment.id} data-segment-id={segment.id}>
                        <g
                            data-object-id={segment.id}
                            data-parent-wall-id={id}
                            data-object-type="frameless_wall_segment"
                            className="cursor-pointer"
                            style={{ cursor: "pointer" }}
                            onClick={(e) => {
                                e.stopPropagation();
                                // Если размещается ролета, не обрабатываем клик на сегменте стены
                                // Ролета должна размещаться только при клике на окно
                                if (elementTypeToPlace === OBJECT_TYPES.WINDOW_BLINDS) {
                                    return;
                                }
                                if (isInPlacementMode) {
                                    if (onWallSegmentClick) {
                                        onWallSegmentClick(segment.id);
                                    }
                                } else {
                                    onSelectWall(segment.id);
                                }
                            }}
                        >
                            {segmentsToRender.map((seg, idx) => (
                                <WallSvg
                                    key={`${segment.id}-${idx}`}
                                    x={seg.start}
                                    y={-wallThicknessPx / 2}
                                    width={Math.max(seg.end - seg.start, 0)}
                                    height={wallThicknessPx}
                                    preserveAspectRatio="none"
                                    style={{ cursor: "pointer" }}
                                />
                            ))}

                            {/* Не подсвечиваем сегмент стены, если размещается ролета - подсвечиваются только окна */}
                            {(isSegmentSelected ||
                                (highlightForPlacement && isInPlacementMode && elementTypeToPlace !== OBJECT_TYPES.WINDOW_BLINDS)) && (
                                    <rect
                                        x={segmentStartPx}
                                        y={-wallThicknessPx / 2}
                                        width={segmentLengthPx}
                                        height={wallThicknessPx}
                                        fill={
                                            highlightForPlacement && isInPlacementMode && elementTypeToPlace !== OBJECT_TYPES.WINDOW_BLINDS
                                                ? PLACEMENT_HIGHLIGHT_FILL_COLOR
                                                : isSegmentSelected
                                                    ? "rgba(0, 123, 255, 0.3)"
                                                    : "transparent"
                                        }
                                        stroke={isSegmentSelected ? SELECTED_ELEMENT_COLOR : "none"}
                                        strokeWidth="1.5"
                                        pointerEvents="none"
                                    />
                                )}
                        </g>

                        {currentElements.map((element) => {
                            // Используем CustomElementRenderer для неизвестных типов
                            const ElementComponent = renderers[element.type] || CustomElementRenderer;

                            const positionOnSegment =
                                typeof element.positionOnSegment === "number"
                                    ? element.positionOnSegment
                                    : 0.5;
                            const elementCenterPx =
                                segmentStartPx + positionOnSegment * segmentLengthPx;

                            // Определяем базовую сторону размещения
                            const basePlacementSide = element.placementSide === 0
                                ? 1
                                : element.placementSide ?? 1;

                            // ПРОСТОЕ РЕШЕНИЕ: Используем basePlacementSide БЕЗ ИЗМЕНЕНИЙ
                            // projectReducer.js уже устанавливает правильное значение
                            const finalPlacementSideFactor = basePlacementSide;

                            const windowShouldFlip = finalPlacementSideFactor === 1;

                            // Подсветка окон для размещения ролеты (как в модульном режиме)
                            const shouldHighlightWindowForBlinds = highlightForPlacement &&
                                (element.type === OBJECT_TYPES.WINDOW || element.type === OBJECT_TYPES.PANORAMIC_WINDOW) &&
                                !currentElements.some(el =>
                                    el.type === OBJECT_TYPES.WINDOW_BLINDS &&
                                    Math.abs((el.positionOnSegment || 0.5) - positionOnSegment) < 0.1
                                );

                            const associatedWindow = element.type === OBJECT_TYPES.WINDOW_BLINDS
                                ? currentElements.find(
                                    w => (w.type === OBJECT_TYPES.WINDOW || w.type === OBJECT_TYPES.PANORAMIC_WINDOW) &&
                                        Math.abs((w.positionOnSegment || 0.5) - positionOnSegment) < 0.01
                                )
                                : null;

                            // Don't render adjacent copies of panoramic windows, but allow other elements (like window blinds)
                            if (element.isAdjacentCopy && element.type === OBJECT_TYPES.PANORAMIC_WINDOW) return null;

                            // Для панорамных окон и ролеты передаем isFrameless=true (безкаркасный режим)
                            const isPanoramicWindow = element.type === OBJECT_TYPES.PANORAMIC_WINDOW;
                            const isWindowBlinds = element.type === OBJECT_TYPES.WINDOW_BLINDS;
                            const additionalProps = (isPanoramicWindow || isWindowBlinds) ? { isFrameless: true } : {};

                            return (
                                <g
                                    key={element.id}
                                    transform={`translate(${elementCenterPx}, 0)`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onSelectElement(element.id);
                                    }}
                                    onContextMenu={(e) =>
                                        onContextMenu(e, element.id, element.type)
                                    }
                                    style={{ cursor: "pointer" }}
                                    data-object-id={element.id}
                                    data-object-type={element.type}
                                >
                                    <ElementComponent
                                        element={element}
                                        scale={scale}
                                        isSelected={selectedElementId === element.id}
                                        wallThickness={isInternalWall ? CORRIDOR_WALL_THICKNESS_M : WALL_THICKNESS_M_RENDER}
                                        placementSideFactor={finalPlacementSideFactor}
                                        orientation={orientation}
                                        windowVisualNeedsFlip={windowShouldFlip}
                                        highlightForBlindsPlacement={shouldHighlightWindowForBlinds}
                                        associatedWindow={associatedWindow}
                                        {...additionalProps}
                                    />
                                </g>
                            );
                        })}
                    </g>
                );
            })}
        </g>
    );
};

export default React.memo(FramelessWallRenderer);